import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { EditStateService } from 'src/app/features/learner-profile/services/edit-state.service';
import { AuthService } from '../../services/auth/auth.service';
import { PushNofiticationsService } from '../../services/communication/push-nofitications.service';
import { NotificationService } from '../../services/notifications/notifications.service';
import { StudentService } from '../../services/student/student.service';
import { UserService } from '../../services/user/user.service';
import { ImageCacheActions } from '../image-cache/image-cache.actions';
import { StudentActions } from '../students/student.actions';
import { GuardianActions } from './guardian.actions';
import { selectSelectedGuardian } from './guardian.selectors';

// NgRx effects are registered via provideEffects(), not the Angular injector — providedIn is not applicable
// eslint-disable-next-line @angular-eslint/use-injectable-provided-in
@Injectable()
export class GuardianEffects {
  private readonly actions$ = inject(Actions);
  private readonly userService = inject(UserService);
  private readonly student = inject(StudentService);
  private readonly notificationService = inject(NotificationService);
  private readonly authService = inject(AuthService);
  private readonly store = inject(Store);
  private readonly pushNotificationService = inject(PushNofiticationsService);
  protected readonly editService = inject(EditStateService);
  private readonly router = inject(Router);

  isProfilePictureUpdate = false;

  loadGuardians$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuardianActions.loadGuardians),
      switchMap(() => {
        const data = this.userService.getGuardians();
        if (data) {
          return data.pipe(
            map((data) => GuardianActions.loadGuardiansSuccess({ data })),
            catchError((error) => of(GuardianActions.loadGuardiansFailure({ error }))),
          );
        } else {
          return of(
            GuardianActions.loadGuardiansFailure({
              error: 'Guardian data not found',
            }),
          );
        }
      }),
    ),
  );

  loadLoggedInGuardian$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuardianActions.loadLoggedinGuardian),
      switchMap(() => {
        return this.authService.getCurrentUser().pipe(
          switchMap((userData) => {
            if (!userData) {
              return of(
                GuardianActions.loadGuardiansFailure({
                  error: 'User data not found',
                }),
              );
            }
            return this.userService.getLoggedInUserInfo(userData.idnumber).pipe(
              map((guardianData) => {
                if (guardianData) {
                  return GuardianActions.loadLoggedinGuardianSuccess({
                    data: guardianData,
                  });
                } else {
                  return GuardianActions.loadGuardiansFailure({
                    error: 'Guardian data not found',
                  });
                }
              }),
              catchError((error) => of(GuardianActions.loadGuardiansFailure({ error }))),
            );
          }),
          catchError((error) => of(GuardianActions.loadGuardiansFailure({ error }))),
        );
      }),
    ),
  );

  setStudents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuardianActions.loadLoggedinGuardianSuccess),
      map(() => StudentActions.loadStudents()),
    ),
  );

  setGuardians$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StudentActions.loadStudentsSuccess),
      map(() => GuardianActions.loadGuardians()),
    ),
  );

  registerForPushNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuardianActions.loadLoggedinGuardianSuccess),
      map(() => {
        if (Capacitor.isNativePlatform()) {
          this.pushNotificationService.initGroupRegistration();
        }
        return { type: '[Guardian] Push Notifications Registered' };
      }),
    ),
  );

  updateGuardian$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuardianActions.updateGuardian),
      mergeMap(({ guardian }) => {
        const data = this.userService.updateUserInfo(guardian);
        return data.pipe(
          map(() => {
            return GuardianActions.updateGuardianSuccess({ guardian });
          }),
          catchError((error) => of(GuardianActions.updateGuardianFailure({ error }))),
        );
      }),
    ),
  );

  updateLearnerAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuardianActions.updateLearnerAccess),
      mergeMap(({ guardian }) => {
        return this.userService.updateLearnerAccess(guardian.idPassport, guardian.learnerAccess).pipe(
          map(() => {
            return GuardianActions.updateLearnerAccessSuccess({ guardian });
          }),
          catchError((error) => of(GuardianActions.updateLearnerAccessFailure({ error }))),
        );
      }),
    ),
  );

  updateGuardianPicture$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuardianActions.updateGuardianPicture),
      mergeMap((action) => {
        const { guardianid, pictureFile, pictureId, pictureUrl } = action;
        const fileName = guardianid;

        return this.store.select(selectSelectedGuardian).pipe(
          take(1),
          mergeMap((guardian) => {
            if (!guardian) {
              return of(
                GuardianActions.updateGuardianFailure({
                  error: 'No guardian selected',
                }),
              );
            }

            // Case 1: Delete existing image (pictureFile is null)
            if (!pictureFile && pictureId) {
              return this.student.deleteProfileImage(pictureId).pipe(
                map(() => {
                  const updatedGuardian = {
                    ...guardian,
                    pictureId: null,
                    pictureUrl: '',
                  };

                  this.store.dispatch(
                    ImageCacheActions.profileImageUpdated({
                      userId: guardian.id,
                      oldImageUrl: typeof guardian.pictureUrl === 'string' ? guardian.pictureUrl : undefined,
                      newImageUrl: pictureUrl,
                    }),
                  );

                  // Force refresh current image (useful for retry scenarios)
                  // refreshCurrentImage(imageUrl: string): void {
                  //     this.store.dispatch(ImageCacheActions.clearImageCache({ imageUrl }));
                  // }

                  return GuardianActions.updateGuardianSuccess({
                    guardian: updatedGuardian,
                  });
                }),
                catchError((error) => {
                  console.error('Delete profile image error:', error);
                  return of(
                    GuardianActions.updateGuardianFailure({
                      error: error?.message || 'Failed to delete profile image',
                    }),
                  );
                }),
              );
            }
            // Case 2: Update existing image
            else if (pictureFile) {
              return this.student.updateProfileImage(pictureFile, fileName, guardianid, pictureId ?? '').pipe(
                map((response) => {
                  const updatedGuardian = {
                    ...guardian,
                    pictureId: response.id ?? guardian.pictureId,
                    pictureUrl: pictureUrl,
                  };
                  this.isProfilePictureUpdate = true;

                  this.store.dispatch(
                    ImageCacheActions.profileImageUpdated({
                      userId: guardian.id,
                      oldImageUrl: typeof guardian.pictureUrl === 'string' ? guardian.pictureUrl : undefined,
                      newImageUrl: pictureUrl,
                    }),
                  );

                  // Force refresh current image (useful for retry scenarios)
                  // refreshCurrentImage(imageUrl: string): void {
                  //     this.store.dispatch(ImageCacheActions.clearImageCache({ imageUrl }));
                  // }

                  return GuardianActions.updateGuardianSuccess({
                    guardian: updatedGuardian,
                  });
                }),
                catchError((error) => {
                  console.error('Update profile image error:', error);
                  return of(
                    GuardianActions.updateGuardianFailure({
                      error: error?.message || 'Failed to update profile image',
                    }),
                  );
                }),
              );
            }
            // No action needed
            else {
              return of(
                GuardianActions.updateGuardianFailure({
                  error: 'Invalid operation: No file provided and no existing image to delete',
                }),
              );
            }
          }),
        );
      }),
    ),
  );

  updateGuardianEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuardianActions.updateGuardianEmail),
      mergeMap(({ guardian }) => {
        const data = this.userService.updateUserInfoEmail(guardian);
        return data.pipe(
          map(() => {
            return GuardianActions.updateGuardianSuccess({ guardian });
          }),
          catchError((error) => of(GuardianActions.updateGuardianFailure({ error }))),
        );
      }),
    ),
  );

  updateGuardianPhone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuardianActions.updateGuardianPhone),
      mergeMap(({ guardian }) => {
        const data = this.userService.updateUserInfoPhone(guardian);
        return data.pipe(
          map(() => {
            return GuardianActions.updateGuardianSuccess({ guardian });
          }),
          catchError((error) => of(GuardianActions.updateGuardianFailure({ error }))),
        );
      }),
    ),
  );

  addPersonPhoneNumber$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuardianActions.addPersonPhoneNumber),
      mergeMap(({ guardian }) => {
        const data = this.userService.addPersonPhoneNumber(guardian);
        return data.pipe(
          map(() => {
            return GuardianActions.updateGuardianSuccess({ guardian });
          }),
          catchError((error) => of(GuardianActions.updateGuardianFailure({ error }))),
        );
      }),
    ),
  );

  deletePhoneNumberById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuardianActions.deletePhoneNumberByID),
      mergeMap(({ phoneNumberId }) => {
        return this.userService.deletePhoneNumberById(phoneNumberId).pipe(
          map((response) => {
            if (response.deleted) {
              this.notificationService.showSnackBar({
                message: 'Phone number deleted successfully!',
                type: 'success',
              });
              return GuardianActions.deletePhoneNumberSuccess({
                phoneNumberId,
              });
            } else {
              return GuardianActions.deletePhoneNumberFailure({
                error: 'Failed to delete phone number',
              });
            }
          }),
          catchError((error) => of(GuardianActions.deletePhoneNumberFailure({ error }))),
        );
      }),
    ),
  );

  deletePhoneNumberFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuardianActions.deletePhoneNumberFailure),
      map(({ error }) => {
        console.error('Error deleting phone number:', error);
        this.notificationService.showSnackBar({
          message: 'Failed to delete phone number',
          type: 'error',
        });
        return { type: '[Guardian] Notification Shown' };
      }),
    ),
  );

  updateGuardianSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuardianActions.updateGuardianSuccess),
      map(() => {
        const message = this.guardianUpdateRouteCheck();
        this.notificationService.showSnackBar({
          message,
          type: 'success',
        });

        this.editService.isEditing.set(false);
        this.handleSuccessfulGuardianBackNavigation();
        return { type: '[Guardian] Notification Shown' };
      }),
    ),
  );

  updateLearnerAccessSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuardianActions.updateLearnerAccessSuccess),
      map(() => {
        this.notificationService.showSnackBar({
          message: 'Student access updated successfully',
          type: 'success',
        });
        return { type: '[Guardian] Notification Shown' };
      }),
    ),
  );

  updateGuardianFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuardianActions.updateGuardianFailure),
      map(({ error }) => {
        console.error('Error updating guardian:', error);
        this.notificationService.showSnackBar({
          message: 'Failed to update guardian',
          type: 'error',
        });
        this.editService.isEditing.set(true);
        return { type: '[Guardian] Notification Shown' };
      }),
    ),
  );

  updateLearnerAccessFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuardianActions.updateLearnerAccessFailure),
      map(({ error }) => {
        console.error('Error updating learner access:', error);
        this.notificationService.showSnackBar({
          message: 'Failed to update student access',
          type: 'error',
        });
        return { type: '[Guardian] Notification Shown' };
      }),
    ),
  );

  sendGuardianInvite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuardianActions.sendGuardianInvite),
      mergeMap(({ guardian }) => {
        return this.userService.sendInviteToGuardian(guardian).pipe(
          map((response) => {
            // Check if the response indicates failure
            if (!response || response.success === false || response.error) {
              throw new Error(response?.error || 'Failed to send guardian invite');
            }

            const updatedGuardian = {
              ...guardian,
              status: 'PEN',
            };
            return GuardianActions.sendGuardianInviteSuccess({
              guardian: updatedGuardian,
            });
          }),
          catchError((error) => {
            console.error('Caught error in sendGuardianInvite$:', error);

            // Extract the actual error message
            const errorMessage =
              typeof error === 'string' ? error : error.message || error.error || 'Failed to send guardian invite';

            return of(GuardianActions.sendGuardianInviteFailure({ error: errorMessage }));
          }),
        );
      }),
    ),
  );

  sendGuardianInviteSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuardianActions.sendGuardianInviteSuccess),
      map(({ guardian }) => {
        this.notificationService.showSnackBar({
          message: `Invite sent successfully to ${guardian.firstname} ${guardian.lastname}`,
          type: 'success',
        });
        return { type: '[Guardian] Notification Shown' };
      }),
    ),
  );

  sendGuardianInviteFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuardianActions.sendGuardianInviteFailure),
      map(({ error }) => {
        console.error('Error sending guardian invite:', error);
        this.notificationService.showSnackBar({
          message: 'Failed to send guardian invite',
          type: 'error',
        });
        return { type: '[Guardian] Notification Shown' };
      }),
    ),
  );

  private guardianUpdateRouteCheck(): string {
    const currentUrl = this.router.url;

    if (this.isProfilePictureUpdate) {
      this.isProfilePictureUpdate = false;
      return 'Success! Profile picture updated';
    }

    // Check for specific routes and return appropriate messages
    if (currentUrl.includes('residential-info')) {
      return 'Success! Residential information updated';
    }
    if (currentUrl.includes('contact-info')) {
      return 'Success! Contact information updated';
    }
    if (currentUrl.includes('personal-info')) {
      return 'Success! Personal information updated';
    }
    if (currentUrl.includes('employment-details')) {
      return 'Success! Employment details updated';
    }

    return 'Guardian updated successfully';
  }

  private handleSuccessfulGuardianBackNavigation(): void {
    const currentUrl = this.router.url;
    if (currentUrl.includes('residential-info') || currentUrl.includes('personal-info')) {
      window.history.back();
    }
  }
}
